import { Injectable, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AdminClientToServerEvents, AdminInBot, AdminServerToClientEvents, WsClient } from '@shared/index';
import { Subject } from 'rxjs';
import { UsertokenService } from './usertoken.service';

export type MySocket = WsClient<AdminServerToClientEvents, AdminClientToServerEvents>;

@Injectable({
  providedIn: 'root'
})
export class SocketAdminService {
  private ioSocket?: MySocket;
  public connectedAdminSockets = signal(0);
  public connectedAdminsCount = signal(0);
  public connectedUserSockets = signal(0);
  public connectedLiveAvatarSessions = signal(0);
  public adminsAreCurrentlyHere: AdminInBot[] = [];
  public onUploadStatus: Subject<Parameters<AdminServerToClientEvents["uploadStatus"]>[0]> = new Subject<Parameters<AdminServerToClientEvents["uploadStatus"]>[0]>();
  public onSupervisorHistoryItemUpdate: Subject<Parameters<AdminServerToClientEvents["supervisorHistoryItemUpdate"]>[0]> = new Subject<Parameters<AdminServerToClientEvents["supervisorHistoryItemUpdate"]>[0]>();
  public onSupervisorHistoryItemDelete: Subject<Parameters<AdminServerToClientEvents["supervisorHistoryItemDelete"]>[0]> = new Subject<Parameters<AdminServerToClientEvents["supervisorHistoryItemDelete"]>[0]>();
  public onSupervisorActiveThreads: Subject<Parameters<AdminServerToClientEvents["supervisorActiveThreads"]>[0]> = new Subject<Parameters<AdminServerToClientEvents["supervisorActiveThreads"]>[0]>();
  public onSupervisorHelpRequest: Subject<Parameters<AdminServerToClientEvents["supervisorHelpRequest"]>[0]> = new Subject<Parameters<AdminServerToClientEvents["supervisorHelpRequest"]>[0]>();
  public onSupervisorUserAnswered: Subject<Parameters<AdminServerToClientEvents["supervisorMessageUserAnswer"]>[0]> = new Subject<Parameters<AdminServerToClientEvents["supervisorMessageUserAnswer"]>[0]>();
  public onSocketEvent: Subject<Parameters<AdminServerToClientEvents["socketEvent"]>[0]> = new Subject<Parameters<AdminServerToClientEvents["socketEvent"]>[0]>();
  public onThreadSummaryUpdate: Subject<Parameters<AdminServerToClientEvents["threadSummaryItemUpdate"]>[0]> = new Subject<Parameters<AdminServerToClientEvents["threadSummaryItemUpdate"]>[0]>();
  public onLogoutOldSession: Subject<Parameters<AdminServerToClientEvents["logoutoldsession"]>[0]> = new Subject<Parameters<AdminServerToClientEvents["logoutoldsession"]>[0]>();
  public onkeyValChange: Subject<Parameters<AdminServerToClientEvents["keyValChange"]>[0]> = new Subject<Parameters<AdminServerToClientEvents["keyValChange"]>[0]>();
  public onkeyValClean: Subject<Parameters<AdminServerToClientEvents["keyValClean"]>[0]> = new Subject<Parameters<AdminServerToClientEvents["keyValClean"]>[0]>();
  public onkeyValDelete: Subject<{ context: Parameters<AdminServerToClientEvents["keyValDelete"]>[0], keepPersistant: boolean }> = new Subject<{ context: Parameters<AdminServerToClientEvents["keyValDelete"]>[0], keepPersistant: boolean }>();

  public onProjectKeyValChange: Subject<Parameters<AdminServerToClientEvents["projectKeyValChange"]>[0]> = new Subject<Parameters<AdminServerToClientEvents["projectKeyValChange"]>[0]>();
  public onProjectKeyValClean: Subject<Parameters<AdminServerToClientEvents["projectKeyValClean"]>[0]> = new Subject<Parameters<AdminServerToClientEvents["projectKeyValClean"]>[0]>();
  public onProjectKeyValDelete: Subject<Parameters<AdminServerToClientEvents["projectKeyValDelete"]>[0]> = new Subject<Parameters<AdminServerToClientEvents["projectKeyValDelete"]>[0]>();

  public onUserListItemUpdate: Subject<Parameters<AdminServerToClientEvents["userListItemUpdate"]>[0]> = new Subject<Parameters<AdminServerToClientEvents["userListItemUpdate"]>[0]>();
  public onUserListItemDelete: Subject<Parameters<AdminServerToClientEvents["userListItemDelete"]>[0]> = new Subject<Parameters<AdminServerToClientEvents["userListItemDelete"]>[0]>();


  public onStageCreated: Subject<Parameters<AdminServerToClientEvents["stageCreated"]>[0]> = new Subject<Parameters<AdminServerToClientEvents["stageCreated"]>[0]>();
  public onStageFieldUpdate: Subject<Parameters<AdminServerToClientEvents["stageFieldUpdate"]>[0]> = new Subject<Parameters<AdminServerToClientEvents["stageFieldUpdate"]>[0]>();
  public onStageDeleted: Subject<Parameters<AdminServerToClientEvents["stageDelete"]>[0]> = new Subject<Parameters<AdminServerToClientEvents["stageDelete"]>[0]>();
  public onStageSkillCleanup: Subject<Parameters<AdminServerToClientEvents["stageSkillCleanup"]>[0]> = new Subject<Parameters<AdminServerToClientEvents["stageSkillCleanup"]>[0]>();



  constructor(public route: ActivatedRoute, private usertokenService: UsertokenService) { }

  private supervisorViewRejoinHandler?: () => void;

  public async joinSupervisorView(botId: string): Promise<void> {
    const io = await this.getIo();
    if (this.supervisorViewRejoinHandler) {
      io.off('connect', this.supervisorViewRejoinHandler);
    }
    // WsClient drops emits sent before the socket is OPEN (no buffering) and doesn't
    // re-join rooms after a reconnect — rebind on every 'connect' so the join survives both.
    this.supervisorViewRejoinHandler = () => io.emit('joinSupervisorView', { botId });
    io.on('connect', this.supervisorViewRejoinHandler);
    if (io.connected) {
      this.supervisorViewRejoinHandler();
    }
  }

  public async leaveSupervisorView(botId: string): Promise<void> {
    const io = await this.getIo();
    if (this.supervisorViewRejoinHandler) {
      io.off('connect', this.supervisorViewRejoinHandler);
      this.supervisorViewRejoinHandler = undefined;
    }
    io.emit('leaveSupervisorView', { botId });
  }

  public async getIo(): Promise<MySocket> {
    if (this.ioSocket) {
      return this.ioSocket;
    } else {
      this.connect();
      return this.getIo();
    }
  }

  public connect() {
    if (this.ioSocket != null) {
      return;
    }

    this.ioSocket = new WsClient<AdminServerToClientEvents, AdminClientToServerEvents>({
      autoConnect: true,
      path: '/api/ws-admin',
      extraHeaders: {
        Ut: "" + this.usertokenService.getUserToken()
      },
      // Re-read the token on every reconnect (timer-, visibility- or
      // online-triggered) instead of reusing the snapshot taken above —
      // without this, a socket that outlives the token's lifetime (1 day)
      // reconnects forever with an already-expired token.
      refreshAuthParams: () => ({ Ut: "" + this.usertokenService.getUserToken() })
    });

    this.ioSocket.on('botConfigChanged', (_data) => { });

    this.ioSocket.on('botUserInteraction', (_data) => { });

    this.ioSocket.on('socketStats', (data) => {
      this.connectedAdminSockets.set(data.connectedAdminSockets);
      this.connectedAdminsCount.set(data.connectedAdminsCount);
      this.connectedUserSockets.set(data.connectedUserSockets);
      this.connectedLiveAvatarSessions.set(data.connectedLiveAvatarSessions);
      this.adminsAreCurrentlyHere = data.hereAreTheAdmins;
    });

    this.ioSocket.on("userListItemUpdate", (data) => {
      this.onUserListItemUpdate.next(data);
    });

    this.ioSocket.on("userListItemDelete", (data) => {
      this.onUserListItemDelete.next(data);
    });

    this.ioSocket.on('uploadStatus', (data) => {
      this.onUploadStatus.next(data);
    });

    this.ioSocket.on('connect', () => { });

    this.ioSocket.on('disconnect', () => { });

    this.ioSocket.on('supervisorHistoryItemUpdate', (data) => {
      this.onSupervisorHistoryItemUpdate.next(data);
    });

    this.ioSocket.on('supervisorHistoryItemDelete', (data) => {
      this.onSupervisorHistoryItemDelete.next(data);
    });

    this.ioSocket.on('supervisorActiveThreads', (data) => {
      this.onSupervisorActiveThreads.next(data);
    });

    this.ioSocket.on('supervisorHelpRequest', (data) => {
      this.onSupervisorHelpRequest.next(data);
    });

    this.ioSocket.on('supervisorMessageUserAnswer', (data) => {
      this.onSupervisorUserAnswered.next(data);
    });

    this.ioSocket.on('socketEvent', (data) => {
      this.onSocketEvent.next(data);
    });

    this.ioSocket.on('threadSummaryItemUpdate', (data) => {
      this.onThreadSummaryUpdate.next(data);
    });

    this.ioSocket.on("logoutoldsession", (data) => {
      this.onLogoutOldSession.next(data);
    });

    this.ioSocket.on("keyValChange", (data) => {
      this.onkeyValChange.next(data);
    });

    this.ioSocket.on("keyValClean", (data) => {
      this.onkeyValClean.next(data);
    });

    this.ioSocket.on("keyValDelete", (data, keepPersistant) => {
      this.onkeyValDelete.next({ context: data, keepPersistant });
    });

    this.ioSocket.on("projectKeyValChange", (data) => {
      this.onProjectKeyValChange.next(data);
    });

    this.ioSocket.on("projectKeyValClean", (data) => {
      this.onProjectKeyValClean.next(data);
    });

    this.ioSocket.on("projectKeyValDelete", (data) => {
      this.onProjectKeyValDelete.next(data);
    });

    this.ioSocket.on('stageCreated', (data) => { this.onStageCreated.next(data); });
    this.ioSocket.on('stageFieldUpdate', (data) => { this.onStageFieldUpdate.next(data); });
    this.ioSocket.on('stageDelete', (data) => { this.onStageDeleted.next(data); });
    this.ioSocket.on('stageSkillCleanup', (data) => { this.onStageSkillCleanup.next(data); });
  }
}
